import { wrapper, createApiClient, Result, ApiError } from "../base";
import BasePricePlanMinimalDTO = Modules.BasePrice.Data.BasePricePlanMinimalDTO;
import BasePricePlanDTO = Modules.BasePrice.Data.BasePricePlanDTO;
import BasePriceImpressionsStatusDTO = Modules.BasePrice.Data.BasePriceImpressionsStatusDTO;
import BasePriceDailyImpressionsDTO = Modules.BasePrice.Data.BasePriceDailyImpressionsDTO;
import BasePricePreviousYearDistributionDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearDistributionDTO;
import BasePricePreviousYearAverageDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearAverageDTO;
import ResolvedPlanDTO = Modules.BasePrice.Data.Resolved.ResolvedPlanDTO;
import ResolvedBrutoPlanDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoPlanDTO;
import PricingModel = Modules.BasePrice.Data.PricingModel;
import BasePriceOverrideDTO = Modules.BasePrice.Data.Overrides.BasePriceOverrideDTO;
import BasePriceBookDTO = Modules.BasePrice.Data.Books.BasePriceBookDTO;
import HierarchyDefinitionDO = App.Data.Hierarchy.HierarchyDefinitionDO;
import Currency = Modules.BasePrice.Data.Currency;

export type NewPlan = {
	year: number;
	name: string;
	hierarchy_definition_id: number;
	/** Required for a netto plan; a bruto plan has no target. */
	target?: number;
	currency: Currency;
	pricing_model: PricingModel;
};

export type ResolvePlanDraft = {
	target?: number;
	books?: BasePriceBookDTO[];
	overrides?: BasePriceOverrideDTO[];
};

const basePath = `base-prices`;
const baseClient = createApiClient();

export default class {
	/**
	 * The plans worth opening, or — with `trashed` — the ones somebody threw
	 * away. One pricing model at a time; the server defaults to netto.
	 */
	getPlans(
		trashed = false,
		pricingModel: PricingModel = "netto"
	): Promise<Result<BasePricePlanMinimalDTO[], ApiError>> {
		return wrapper(
			baseClient.get(basePath, {
				params: {
					pricing_model: pricingModel,
					...(trashed ? { trashed: 1 } : {}),
				},
			})
		);
	}

	/** The screen-level hierarchies a plan can be built on — see BasePriceController::hierarchyDefinitions(). */
	getHierarchyDefinitions(): Promise<
		Result<HierarchyDefinitionDO[], ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/hierarchy-definitions`));
	}

	/** Creates an empty plan that starts a chain of its own. */
	createPlan(plan: NewPlan): Promise<Result<BasePricePlanDTO, ApiError>> {
		return wrapper(baseClient.post(basePath, plan));
	}

	getPlan(uuid: string): Promise<Result<BasePricePlanDTO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${uuid}`));
	}

	/**
	 * Every version of this plan, newest first — the whole chain it belongs to,
	 * archived versions included, which is what separates this from getPlans().
	 * See BasePriceController::versions().
	 */
	getPlanVersions(
		uuid: string
	): Promise<Result<BasePricePlanMinimalDTO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${uuid}/versions`));
	}

	/**
	 * Previews a not-yet-saved draft: the whole thing, not just the target,
	 * since a target edit, a reservation-book carve-out and a pending override
	 * edit can all be in flight at once and none should silently discard the
	 * others. Resolved against the in-memory model only, never persisted — see
	 * BasePriceController::resolve().
	 */
	/**
	 * Saves the working draft as a *new* plan and opens it, archiving the one it
	 * was saved from — see BasePricePlanWriter. Returns the plan that was
	 * created, which has a different uuid to the one in the path.
	 */
	savePlan(
		uuid: string,
		draft: ResolvePlanDraft
	): Promise<Result<BasePricePlanDTO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/${uuid}/save`, draft));
	}

	/**
	 * Throws a plan away. The row survives — it only changes status, so versions
	 * saved from it still make sense — but it leaves the overview for good. See
	 * BasePriceController::destroy().
	 */
	deletePlan(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/${uuid}`));
	}

	/**
	 * Brings a deleted plan back as the live version of its chain. Refused when
	 * that chain already has one — see BasePriceController::restore().
	 */
	restorePlan(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/${uuid}/restore`));
	}

	/**
	 * Whether the live source has moved away from the impressions this version
	 * prices against. Recomputed on the server every call; saves nothing. See
	 * BasePriceController::impressionsStatus().
	 */
	getImpressionsStatus(
		uuid: string
	): Promise<Result<BasePriceImpressionsStatusDTO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${uuid}/impressions-status`));
	}

	/**
	 * What earlier years looked like for one target in one book — the source
	 * for "Derive distribution from previous years". See
	 * BasePricePreviousYearsController.
	 */
	getPreviousYearsDistribution(
		uuid: string,
		body: { label: string; level: number }
	): Promise<Result<BasePricePreviousYearDistributionDTO, ApiError>> {
		return wrapper(
			// Reads two years of revenue through the explorer, which can take
			// well over the default 30s high up the hierarchy.
			baseClient.post(`${basePath}/${uuid}/previous-years-distribution`, body, {
				timeout: 60000,
			})
		);
	}

	/**
	 * Earlier years' average CPM per month for one target — the source for
	 * "Derive CPM from previous years". See BasePricePreviousYearsCpmController.
	 */
	getPreviousYearsCpm(
		uuid: string,
		body: { label: string; level: number }
	): Promise<Result<BasePricePreviousYearAverageDTO, ApiError>> {
		return wrapper(
			// Same explorer read as the distribution, so the same long timeout.
			baseClient.post(`${basePath}/${uuid}/previous-years-cpm`, body, {
				timeout: 60000,
			})
		);
	}

	/**
	 * Earlier years' fill rate per month for one target, 0-100 — the source for
	 * "Derive fill rate from previous years". See
	 * BasePricePreviousYearsFillController.
	 */
	getPreviousYearsFill(
		uuid: string,
		body: { label: string; level: number }
	): Promise<Result<BasePricePreviousYearAverageDTO, ApiError>> {
		return wrapper(
			// Same explorer read as the distribution, so the same long timeout.
			baseClient.post(`${basePath}/${uuid}/previous-years-fill`, body, {
				timeout: 60000,
			})
		);
	}

	/**
	 * One month of gross impressions, day by day, summed over the given screens
	 * — the figures this version prices against. What the Seasonality calendar
	 * spreads a month's budget over. See BasePriceController::dailyImpressions().
	 *
	 * @param month 1-12
	 */
	getDailyImpressions(
		uuid: string,
		month: number,
		assetIds: number[]
	): Promise<Result<BasePriceDailyImpressionsDTO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${uuid}/daily-impressions`, {
				month,
				asset_ids: assetIds,
			})
		);
	}

	/**
	 * Takes the impressions afresh and saves the draft as a new version priced
	 * against them, archiving the one it was saved from — the same as savePlan().
	 * Earlier versions keep the impressions they were saved with. See
	 * BasePriceController::refreshImpressions().
	 */
	refreshImpressions(
		uuid: string,
		draft: ResolvePlanDraft
	): Promise<Result<BasePricePlanDTO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${uuid}/refresh-impressions`, draft)
		);
	}

	/**
	 * Publishes the plan as steering rules — one price per screen per day of its
	 * year, from its open inventory. Queued: answers 202 and reports the outcome
	 * by notification. See BasePriceController::exportSteeringRules().
	 */
	exportSteeringRules(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${uuid}/export-steering-rules`)
		);
	}

	/**
	 * Where the plan's remaining impressions CSV is served — a row per screen,
	 * a column per day. A URL rather than a request: the browser downloads it
	 * itself, so the file streams to disk as the server writes it. See
	 * BasePriceController::exportRemainingImpressions().
	 */
	remainingImpressionsExportUrl(uuid: string): string {
		return `/api/${basePath}/${uuid}/export-remaining-impressions`;
	}

	resolvePlan(
		uuid: string,
		draft: ResolvePlanDraft
	): Promise<Result<ResolvedPlanDTO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/${uuid}/resolve`, draft));
	}

	/**
	 * The same endpoint as resolvePlan(), for a bruto plan: the CPM in effect per
	 * node and screen per month, from the draft's CPM overrides.
	 */
	resolveBrutoPlan(
		uuid: string,
		draft: Pick<ResolvePlanDraft, "overrides">
	): Promise<Result<ResolvedBrutoPlanDTO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/${uuid}/resolve`, draft));
	}
}
